import * as React from "react"
import styled, { css } from "@digits-css"
import {
  svgBrandedPathStyles,
  svgIconStyles,
  svgPathStyles,
} from "@digits-shared/components/SVG/svgIconStyles"
import { ButtonContent } from "@digits-shared/DesignSystem/ButtonContent"
import {
  GLASS_SHEEN_BACKGROUND,
  GLASS_STROKE_EDGE_OVERLAY,
} from "@digits-shared/DesignSystem/Containers/Glass"
import colorHelper from "@digits-shared/helpers/colorHelper"
import { BLUR_XL } from "@digits-shared/themes/blur"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"

export type ButtonVariant =
  | "primary"
  | "primary-blue"
  | "primary-outline"
  | "secondary-dark"
  | "secondary-light"
  | "secondary-white"
  | "secondary-error"
  | "ghost-dark"
  | "ghost-light"
  | "ghost-error"
  | "ghost-inline"
  | "error"
  | "white"
  | "ai"
  | "glass"

export type ButtonSize = "micro" | "small" | "medium" | "default" | "large"

export interface ButtonProps {
  size?: ButtonSize
  width?: number
  $variant?: ButtonVariant
  $fullWidth?: boolean
  disabled?: boolean
  $active?: boolean
  $circle?: boolean
  $solidIcon?: boolean
}

export type DigitsButtonProps<T = React.ButtonHTMLAttributes<HTMLButtonElement>> = ButtonProps &
  T & {
    className?: string
    loading?: boolean
    label?: string
    leftDecorator?: React.ReactNode
  }

export const DIGITS_BUTTON_STYLE = css`
  position: relative;
  transition: background 275ms ease;

  padding: 0;
  border: none;
  outline-offset: 0;

  &,
  &:focus,
  &:active {
    outline: none;
  }

  &:focus-visible {
    outline: var(--outline);
  }

  display: flex;
  align-items: center;
  justify-content: center;

  font-weight: ${fonts.weight.heavy} !important;
  white-space: nowrap;

  width: fit-content;
  &[data-full-width] {
    width: 100%;
  }

  border-radius: 32px;

  &:not([disabled]) {
    cursor: pointer;
  }

  &[disabled] {
    pointer-events: none;
  }

  /* ---- Sizes ---- */

  &[data-size="micro"] {
    ${fonts.scale.label}
    padding: 0 4px;
    height: 18px;
    gap: 4px;

    svg {
      width: 12px;
      height: 12px;
      margin-left: -4px;
    }

    &[data-circle] {
      width: 18px;
      min-width: 18px;
      border-radius: 50%;
      padding: 0;
      svg {
        margin-left: unset;
      }
    }
  }

  &[data-size="small"] {
    ${fonts.scale.detail}
    padding: 0 16px;
    height: 24px;
    gap: 4px;

    svg {
      width: 16px;
      height: 16px;
      margin-left: -4px;
    }

    &[data-variant="ghost-dark"],
    &[data-variant="ghost-light"],
    &[data-variant="ghost-error"] {
      padding: 0 12px;
    }

    &[data-variant="ghost-inline"] {
      padding: 0;
    }

    &[data-circle] {
      width: 24px;
      min-width: 24px;
      border-radius: 50%;
      padding: 0;
      svg {
        margin-left: unset;
      }
    }
  }

  &[data-size="medium"] {
    ${fonts.scale.body}
    padding: 0 16px;
    height: 32px;
    gap: 4px;

    svg {
      width: 20px;
      height: 20px;
      margin-left: -3px;
    }

    &[data-variant="ghost-dark"],
    &[data-variant="ghost-light"],
    &[data-variant="ghost-error"] {
      padding: 0 12px;
    }

    &[data-variant="ghost-inline"] {
      padding: 0;
    }

    &[data-circle] {
      width: 32px;
      min-width: 32px;
      border-radius: 50%;
      padding: 0;
      svg {
        margin-left: unset;
      }
    }
  }

  &[data-size="default"],
  &:not([data-size]) {
    ${fonts.scale.body}
    padding: 0 24px;
    height: 40px;
    gap: 6px;

    svg {
      width: 20px;
      height: 20px;
      margin-left: -8px;
    }

    &[data-variant="ghost-dark"],
    &[data-variant="ghost-light"],
    &[data-variant="ghost-error"] {
      padding: 0 16px;
    }

    &[data-variant="ghost-inline"] {
      padding: 0;
    }

    &[data-circle] {
      width: 40px;
      min-width: 40px;
      border-radius: 50%;
      padding: 0;
      svg {
        margin-left: unset;
      }
    }
  }

  &[data-size="large"] {
    ${fonts.scale.body}
    padding: 0 24px;
    height: 48px;
    gap: 6px;

    svg {
      width: 20px;
      height: 20px;
      margin-left: -8px;
    }

    &[data-variant="ghost-dark"],
    &[data-variant="ghost-light"],
    &[data-variant="ghost-error"] {
      padding: 0 16px;
    }

    &[data-variant="ghost-inline"] {
      padding: 0;
    }

    &[data-circle] {
      width: 48px;
      min-width: 48px;
      border-radius: 50%;
      padding: 0;
      svg {
        margin-left: unset;
      }
    }
  }

  /* ---- Variants ---- */

  /* primary (default) */
  &[data-variant="primary"],
  &:not([data-variant]) {
    color: ${colors.secondaryDark};
    background: ${colors.accentGreen};

    &:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.secondaryDark, 1.5)}
    }
    &[data-solid-icon] svg {
      ${svgIconStyles(colors.secondaryDark)}
    }

    &[disabled] {
      color: ${colors.secondary70};
      background: ${colorHelper.hexToRgba(colors.accentGreen, 0.4)};
    }
    &[disabled]:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.secondary70, 1.5)}
    }
    &[disabled][data-solid-icon] svg {
      ${svgIconStyles(colors.secondary70)}
    }

    &[data-active] {
      background: #4bcfad;
    }
    &:hover:not([disabled]):not([data-active]) {
      background: #75ffdc;
    }
  }

  /* primary-blue */
  &[data-variant="primary-blue"] {
    color: ${colors.white};
    background: ${colors.secondary};

    &:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.white, 1.5)}
    }
    &[data-solid-icon] svg {
      ${svgIconStyles(colors.white)}
    }

    &[disabled] {
      color: ${colors.translucentWhite70};
      background: ${colors.secondary40};
    }
    &[disabled]:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.secondary60, 1.5)}
    }
    &[disabled][data-solid-icon] svg {
      ${svgIconStyles(colors.secondary60)}
    }

    svg path.bubble-loader-fill {
      fill: ${colors.white};
    }

    &[data-active] {
      background: #0b1f4d;
    }
    &:hover:not([disabled]):not([data-active]) {
      background: #4e6497;
    }
  }

  /* primary-outline */
  &[data-variant="primary-outline"] {
    color: ${colors.accentGreen};
    background: ${colors.translucentSecondary05};

    &:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.accentTurquoise, 1.5)}
    }
    &[data-solid-icon] svg {
      ${svgIconStyles(colors.accentTurquoise)}
    }

    &[disabled] {
      color: ${colors.accentTurquoise};
    }
    &[disabled] svg {
      opacity: 0.6;
    }
    &[disabled]:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.accentTurquoise, 1.5)}
    }
    &[disabled][data-solid-icon] svg {
      ${svgIconStyles(colors.accentTurquoise)}
    }

    &[data-active] {
      background: ${colors.translucentSecondary20};
    }
    &:hover:not([disabled]):not([data-active]) {
      background: ${colors.translucentSecondary10};
    }
  }

  /* secondary-dark */
  &[data-variant="secondary-dark"] {
    color: ${colors.secondary};
    background: ${colors.translucentSecondary05};

    &:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.secondary, 1.5)}
    }
    &[data-solid-icon] svg {
      ${svgIconStyles(colors.secondary)}
    }

    &[disabled] {
      color: ${colors.secondary60};
    }
    &[disabled]:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.secondary60, 1.5)}
    }
    &[disabled][data-solid-icon] svg {
      ${svgIconStyles(colors.secondary60)}
    }

    &[data-active] {
      background: ${colors.translucentSecondary20};
    }
    &:hover:not([disabled]):not([data-active]) {
      background: ${colors.translucentSecondary10};
    }
  }

  /* secondary-light */
  &[data-variant="secondary-light"] {
    color: ${colors.secondary};
    background: ${colors.translucentWhite40};

    &:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.secondary, 1.5)}
    }
    &[data-solid-icon] svg {
      ${svgIconStyles(colors.secondary)}
    }

    &[disabled] {
      color: ${colors.translucentSecondary60};
      background: ${colors.translucentWhite20};
    }
    &[disabled]:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.secondary60, 1.5)}
    }
    &[disabled][data-solid-icon] svg {
      ${svgIconStyles(colors.secondary60)}
    }

    &[data-active] {
      background: ${colors.translucentWhite80};
    }
    &:hover:not([disabled]):not([data-active]) {
      background: ${colors.secondary05};
    }
  }

  /* white */
  &[data-variant="white"] {
    color: ${colors.secondary};
    background: ${colors.white};

    &:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.secondary, 1.5)}
    }
    &[data-solid-icon] svg {
      ${svgIconStyles(colors.secondary)}
    }

    &[disabled] {
      color: ${colors.translucentSecondary60};
      background: ${colors.translucentWhite20};
    }
    &[disabled]:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.secondary60, 1.5)}
    }
    &[disabled][data-solid-icon] svg {
      ${svgIconStyles(colors.secondary60)}
    }

    &[data-active] {
      background: ${colors.translucentWhite80};
    }
    &:hover:not([disabled]):not([data-active]) {
      background: ${colors.secondary05};
    }
  }

  /* secondary-error */
  &[data-variant="secondary-error"] {
    color: ${colors.errorText};
    background: ${colorHelper.hexToRgba(colors.errorDark, 0.1)};

    &:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.errorText, 1.5)}
    }
    &[data-solid-icon] svg {
      ${svgIconStyles(colors.errorText)}
    }

    &[disabled] {
      color: ${colorHelper.hexToRgba(colors.errorText, 0.6)};
      background: ${colorHelper.hexToRgba(colors.errorDark, 0.05)};
    }
    &[disabled]:not([data-solid-icon]) svg {
      ${svgPathStyles(colorHelper.hexToRgba(colors.errorText, 0.6), 1.5)}
    }
    &[disabled][data-solid-icon] svg {
      ${svgIconStyles(colorHelper.hexToRgba(colors.errorText, 0.6))}
    }

    &[data-active] {
      background: ${colorHelper.hexToRgba(colors.errorDark, 0.2)};
    }
    &:hover:not([disabled]):not([data-active]) {
      background: ${colorHelper.hexToRgba(colors.errorDark, 0.2)};
    }
  }

  /* ghost-dark */
  &[data-variant="ghost-dark"] {
    color: ${colors.secondary};
    background: ${colors.transparent};

    &:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.secondary, 1.5)}
    }
    &[data-solid-icon] svg {
      ${svgIconStyles(colors.secondary)}
    }

    &[disabled] {
      color: ${colors.secondary60};
    }
    &[disabled]:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.secondary60, 1.5)}
    }
    &[disabled][data-solid-icon] svg {
      ${svgIconStyles(colors.secondary60)}
    }

    &[data-active] {
      background: ${colors.translucentSecondary20};
    }
    &:hover:not([disabled]):not([data-active]) {
      background: ${colors.translucentSecondary05};
    }
  }

  /* ghost-light */
  &[data-variant="ghost-light"] {
    color: ${colors.white};
    background: ${colors.transparent};

    &:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.white, 1.5)}
    }
    &[data-solid-icon] svg {
      ${svgIconStyles(colors.white)}
    }

    &[disabled] {
      color: ${colors.translucentWhite60};
    }
    &[disabled]:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.translucentWhite60, 1.5)}
    }
    &[disabled][data-solid-icon] svg {
      ${svgIconStyles(colors.translucentWhite60)}
    }

    &[data-active] {
      background: ${colors.translucentWhite20};
    }
    &:hover:not([disabled]):not([data-active]) {
      background: ${colors.translucentWhite10};
    }
  }

  /* ghost-error */
  &[data-variant="ghost-error"] {
    color: ${colors.orange};
    background: ${colors.transparent};

    &:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.error, 1.5)}
    }
    &[data-solid-icon] svg {
      ${svgIconStyles(colors.error)}
    }

    &[disabled] {
      color: ${colors.translucentOrange70};
    }
    &[disabled]:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.translucentOrange70, 1.5)}
    }
    &[disabled][data-solid-icon] svg {
      ${svgIconStyles(colors.translucentOrange70)}
    }

    &[data-active] {
      background: ${colors.translucentOrange10};
    }
    &:hover:not([disabled]):not([data-active]) {
      background: ${colors.translucentOrange05};
    }
  }

  /* ghost-inline — flows inline with surrounding copy. No padding (set in
     the size rules above), no background ever. Hover and active both shift
     the text + icon to accent blue; the variant relies on color alone for
     interactive affordance, so it's appropriate inside card headers /
     sentences where a button chip would read as visual noise. */
  &[data-variant="ghost-inline"] {
    color: ${colors.secondary};
    background: ${colors.transparent};

    &:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.secondary, 1.5)}
    }
    &[data-solid-icon] svg {
      ${svgIconStyles(colors.secondary)}
    }

    &[disabled] {
      color: ${colors.secondary60};
    }
    &[disabled]:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.secondary60, 1.5)}
    }
    &[disabled][data-solid-icon] svg {
      ${svgIconStyles(colors.secondary60)}
    }

    &[data-active],
    &:hover:not([disabled]) {
      background: ${colors.transparent};
      color: ${colors.aiBlue};
    }
    &[data-active]:not([data-solid-icon]) svg,
    &:hover:not([disabled]):not([data-solid-icon]) svg {
      ${svgPathStyles(colors.aiBlue, 1.5)}
    }
    &[data-active][data-solid-icon] svg,
    &:hover:not([disabled])[data-solid-icon] svg {
      ${svgIconStyles(colors.aiBlue)}
    }

    /* No padding means the focus-visible outline would crop tight against
       the content; offset it outward so the ring doesn't kiss the text or
       a leading/trailing icon. Scale the offset with size — a 4px ring on
       a 40px label looks meaner than the same ring on a 24px one. */
    &:focus-visible {
      outline-offset: 4px;
    }
    &[data-size="medium"]:focus-visible {
      outline-offset: 6px;
    }
    &[data-size="default"]:focus-visible,
    &:not([data-size]):focus-visible {
      outline-offset: 8px;
    }
  }

  /* error */
  &[data-variant="error"] {
    color: ${colors.white};
    background: ${colors.orange};

    &:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.white, 1.5)}
    }
    &[data-solid-icon] svg {
      ${svgIconStyles(colors.white)}
    }

    &[disabled] {
      background: ${colors.translucentOrange70};
    }
    &[disabled]:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.translucentWhite70, 1.5)}
    }
    &[disabled][data-solid-icon] svg {
      ${svgIconStyles(colors.translucentWhite70)}
    }

    &[data-active] {
      background: ${colors.errorDark};
    }
    &:hover:not([disabled]):not([data-active]) {
      background: ${colors.errorDark};
    }
  }

  /* secondary-white */
  &[data-variant="secondary-white"] {
    color: var(--brand-header-text-color, ${colors.white});
    background: ${colors.translucentWhite10};

    &:not([data-solid-icon]) svg {
      ${svgBrandedPathStyles()}
    }
    &[data-solid-icon] svg {
      ${svgIconStyles(`var(--brand-header-text-color, ${colors.white})`)}
    }

    &[disabled] {
      opacity: 0.6;
    }

    &[data-active] {
      background: ${colors.translucentWhite30};
    }
    &:hover:not([disabled]):not([data-active]) {
      background: ${colors.translucentWhite20};
    }
  }

  /* ai */
  &[data-variant="ai"] {
    color: ${colors.white};
    background: #399cd4 linear-gradient(84deg, #399cd4 -4.07%, #5e5be9 95.1%);

    &:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.white, 1.5)}
    }
    &[data-solid-icon] svg {
      ${svgIconStyles(colors.white)}
    }

    &[disabled] {
      color: ${colors.offWhite};
    }
    &[disabled] svg {
      opacity: 0.6;
    }

    &[data-active] {
      background-image: none;
    }
    &:hover:not([disabled]):not([data-active]) {
      background-image: none;
    }
  }

  /* glass */
  &[data-variant="glass"] {
    color: ${colors.white};
    overflow: clip;
    isolation: isolate;
    -webkit-backdrop-filter: blur(${BLUR_XL});
    backdrop-filter: blur(${BLUR_XL});
    background: ${GLASS_SHEEN_BACKGROUND}, ${colors.translucentWhite20};
    background-size: 100% 100%;
    box-sizing: border-box;

    ${GLASS_STROKE_EDGE_OVERLAY}

    // No hover glow on touch devices; a faint drop shadow (Figma "Glass
    // Button Shadow", 5% black) gives the button its pop instead.
    .mobile-only & {
      box-shadow: 0 2px 4px 0 ${colors.translucentBlack06};
    }

    /* Hover glow — horizontal position tracks cursor via --glow-x */
    &::after {
      content: "";
      position: absolute;
      width: 135%;
      height: 110%;
      left: var(--glow-x, 50%);
      top: 50%;
      transform: translate(-50%, -50%);
      pointer-events: none;
      opacity: 0;
      transition: opacity 200ms ease;
      will-change: left, opacity;
      background:
        radial-gradient(
          52.43% 57.5% at 50% 100%,
          ${colorHelper.hexToRgba(colors.white, 0.3)} 0%,
          ${colors.transparent} 100%
        ),
        ${GLASS_SHEEN_BACKGROUND}, ${colors.translucentWhite04};
    }

    &:hover:not([disabled]):not([data-active])::after {
      opacity: 1;
    }

    &:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.white, 1.5)}
    }
    &[data-solid-icon] svg {
      ${svgIconStyles(colors.white)}
    }

    svg path.bubble-loader-fill {
      fill: ${colors.white};
    }

    &[disabled] {
      color: ${colors.translucentWhite60};
      background: ${GLASS_SHEEN_BACKGROUND}, ${colors.translucentWhite10};
    }
    &[disabled]:not([data-solid-icon]) svg {
      ${svgPathStyles(colors.translucentWhite60, 1.5)}
    }
    &[disabled][data-solid-icon] svg {
      ${svgIconStyles(colors.translucentWhite60)}
    }

    &[data-active],
    &:active:not([disabled]) {
      background: ${GLASS_SHEEN_BACKGROUND}, ${colors.translucentWhite30};
    }
  }
`

export const LeftDecorator = styled.div`
  position: absolute;
  display: flex;
  left: 25px;
`

const handleGlassMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
  const rect = e.currentTarget.getBoundingClientRect()
  const cursorX = ((e.clientX - rect.left) / rect.width) * 100
  // Dampen horizontal movement — glow stays in the middle third
  const glowX = 50 + (cursorX - 50) * 0.3
  e.currentTarget.style.setProperty("--glow-x", `${glowX}%`)
}

const LoadingButton = React.forwardRef<HTMLButtonElement, DigitsButtonProps>(
  (
    {
      className,
      loading,
      children,
      leftDecorator,
      label,
      $variant,
      $active,
      $circle,
      $solidIcon,
      $fullWidth,
      size,
      width,
      style,
      ...rest
    },
    ref
  ) => (
    <button
      ref={ref}
      // tab index must be explicitly set for focus to work in Safari
      tabIndex={0}
      className={className}
      {...rest}
      {...($variant === "glass" && { onMouseMove: handleGlassMouseMove })}
      data-loading={Boolean(loading).toString()}
      data-variant={$variant || "primary"}
      data-size={size || "default"}
      data-active={$active || undefined}
      data-circle={$circle || undefined}
      data-solid-icon={$solidIcon || undefined}
      data-full-width={$fullWidth || undefined}
      style={width && !$fullWidth ? { ...style, width: `${width}px` } : style}
    >
      {leftDecorator && <LeftDecorator>{leftDecorator}</LeftDecorator>}
      <ButtonContent size={size || "default"} loading={Boolean(loading)}>
        {label || children}
      </ButtonContent>
    </button>
  )
)

/**
 * Digits Button
 *
 * Defaults to a primary buttons with default size and rounded shape.
 * Buttons can have a custom `width`, but `height` should not be adjusted.
 * If `label` is provided it will be rendered as `children`
 * When `leftDecorator` is provided it will be left justified 25px from the edge and persistently displayed
 * When `loading` is truthy the `children` will be hidden and a loading animation will display in the center, but `leftDecorator` will remain visible
 */
export const DigitsButton = styled(LoadingButton)<ButtonProps>`
  ${DIGITS_BUTTON_STYLE}
`
DigitsButton.displayName = "DigitsButton"

export const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
`
